import React from 'react';
import PropTypes from 'prop-types';
import { Input, Radio } from 'antd';

import { NPI_ERROR_ID, NPI_INPUT_ID, isValidNpi } from '../walkthroughSteps';

/**
 * Provider details: whether the user has an NPI, and the NPI itself.
 *
 * The validation message stays hidden until the field has been blurred or the
 * user has tried to advance, so a pristine step is not pre-yelled at; the
 * container flips `npiTouched` on both. Next is deliberately NOT disabled for an
 * incomplete NPI — a disabled button with no explanation is what made this step
 * a dead end. The container's saveProviderDetails refuses and reveals the hint.
 */
const ProviderStep = ({
  isProvider, npi, npiTouched, onChoice, onNpiBlur, onNpiChange,
}) => {
  const npiInvalid = isProvider && !isValidNpi(npi);
  const showNpiError = npiInvalid && npiTouched;
  return (
    <div className="walkthrough-compact-step walkthrough-provider-step">
      <h2>Provider details</h2>
      <p>An NPI is a 10-digit National Provider Identifier used to identify healthcare providers.</p>
      <Radio.Group value={isProvider ? 'provider' : 'not_provider'} onChange={onChoice}>
        <Radio value="provider" className="walkthrough-radio-card">
          <strong>I have an NPI</strong>
          <span>Enter your 10-digit NPI.</span>
        </Radio>
        <Radio value="not_provider" className="walkthrough-radio-card">
          <strong>I am not a provider</strong>
          <span>You can continue without an NPI.</span>
        </Radio>
      </Radio.Group>
      {/* Rendered outside the Radio: an interactive control nested in a radio's
          label steals the click and breaks the label-to-control association. */}
      {isProvider && (
        <div className="walkthrough-npi-field">
          <Input
            id={NPI_INPUT_ID}
            aria-label="NPI"
            aria-describedby={showNpiError ? NPI_ERROR_ID : undefined}
            aria-invalid={npiInvalid}
            inputMode="numeric"
            maxLength={10}
            placeholder="10-digit NPI"
            value={npi}
            onBlur={onNpiBlur}
            onChange={onNpiChange}
          />
          {showNpiError && (
            <div className="walkthrough-inline-error" id={NPI_ERROR_ID} role="alert">
              Enter your 10-digit NPI, or select that you are not a provider.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

ProviderStep.defaultProps = {
  isProvider: true,
  npi: '',
  npiTouched: false,
};

ProviderStep.propTypes = {
  isProvider: PropTypes.bool,
  npi: PropTypes.string,
  npiTouched: PropTypes.bool,
  onChoice: PropTypes.func.isRequired,
  onNpiBlur: PropTypes.func.isRequired,
  onNpiChange: PropTypes.func.isRequired,
};

export default ProviderStep;
